// frontend/src/components/login/SignUp.jsx
import React, { useEffect, useState, useRef } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { supabase } from "../../supaBaseClient";
import { API_BASE } from "../../config";

// use centralized API_BASE from config

const SignUp = ({ setUserUID }) => {
  const navigate = useNavigate();

  // Form state
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [schoolYear, setSchoolYear] = useState("");
  const [level, setLevel] = useState("Foundation");
  const [isGoogleSignUp, setIsGoogleSignUp] = useState(false);

  // Password requirement UI
  const [showRequirements, setShowRequirements] = useState(false);
  const [requirementsMet, setRequirementsMet] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    special: false,
  });

  // --- Helpers ---
  const validatePassword = (pwd) => {
    const isValid = {
      length: pwd.length >= 8,
      uppercase: /[A-Z]/.test(pwd),
      lowercase: /[a-z]/.test(pwd),
      number: /\d/.test(pwd),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(pwd),
    };
    setRequirementsMet(isValid);
    return Object.values(isValid).every(Boolean);
  };

  // Prevent duplicate toast notifications
  const hasShownGoogleToast = useRef(false);

  // On mount / on redirect back from Google, detect session and pre-fill
  useEffect(() => {
    (async () => {
      const { data: sessionData, error } = await supabase.auth.getSession();
      if (error) {
        console.error("Supabase getSession error:", error);
        return;
      }
      const session = sessionData?.session;
      const user = session?.user;
      if (user && !hasShownGoogleToast.current) {
        // We returned from OAuth. Pre-fill and continue like your old flow.
        const fullName =
          user.user_metadata?.full_name ||
          user.user_metadata?.name ||
          user.user_metadata?.given_name ||
          "";
        const parts = fullName ? fullName.split(" ") : [];
        setFirstName(parts[0] || user.user_metadata?.given_name || "");
        setLastName(parts.slice(1).join(" ") || user.user_metadata?.family_name || "");
        setEmail(user.email || "");
        setIsGoogleSignUp(true);
        hasShownGoogleToast.current = true;
      }
    })();
  }, []);

  // --- Email/Password sign up ---
  const handleEmailSignUp = async (e) => {
    e.preventDefault();
    setShowRequirements(false);

    if (!firstName || !lastName || !email || !password || !schoolYear || !level) {
      return;
    }
    if (!validatePassword(password)) {
      setShowRequirements(true);
      return;
    }

    try {
      const encodedEmail = encodeURIComponent(email);
      const { data: existsData } = await axios.get(
        `${API_BASE}/api/users/email/${encodedEmail}`
      );
      if (existsData?.exists) {
        return;
      }

      // Create Supabase auth user (Supabase will send verification email if enabled)
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/signin`,
          data: {
            first_name: firstName,
            last_name: lastName,
            school_year: schoolYear,
            level,
          },
        },
      });
      if (error) throw error;

      const user = data?.user;
      if (!user) {
        throw new Error("No user returned from Supabase signUp.");
      }

      const uid = user.id; // Supabase UUID
      setUserUID?.(uid);

      toast.dismiss();
      toast.success("Verification link sent to your email!", { autoClose: 1500 });

      // Create user in your backend DB
      await axios.post(`${API_BASE}/api/users`, {
        uid,
        first_name: firstName,
        last_name: lastName,
        email_address: email,
        school_year: schoolYear,
        level,
      });

      navigate("/signin");
    } catch (err) {
      console.error("Error signing up:", err);
    }
  };

  // --- Google Redirect sign up ---
  const handleGoogleRedirect = async () => {
    try {
      // Redirect flow; returns to this same page
      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: `${window.location.origin}/signup`,
          queryParams: {
            // optional but helps refresh tokens
            access_type: "offline",
            prompt: "consent",
          },
        },
      });
      if (error) throw error;
      // No further code runs here—browser will redirect.
    } catch (err) {
      console.error("Error starting Google sign-in:", err);
    }
  };

  // After redirect + prefill, complete the form and save to your DB
  const handleGoogleFormSubmit = async (e) => {
    e.preventDefault();
    if (!firstName || !lastName || !schoolYear || !level) {
      return;
    }

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const user = sessionData?.session?.user;
      if (!user) throw new Error("No active Supabase session.");

      // Create/ensure record in your backend DB
      await axios.post(`${API_BASE}/api/users`, {
        uid: user.id,
        first_name: firstName,
        last_name: lastName,
        email_address: user.email,
        school_year: schoolYear,
        level,
      });

      setUserUID?.(user.id);
      navigate("/home");
    } catch (err) {
      console.error("Error saving Google user:", err);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-100">
      <Helmet>
        <title>Sign Up | MLMaths</title>
        <link rel="canonical" href="https://mlmaths.co.uk/signup" />
        <meta name="description" content="Create your MLMaths account to access GCSE Maths videos, quizzes, and track your learning progress." />
      </Helmet>
      <header className="text-center mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-teal-600">Join MLMaths</h1>
        <p className="mt-2 text-base sm:text-lg text-gray-700">
          {isGoogleSignUp ? "Complete your Google account setup" : "Create your account to start learning"}
        </p>
        {isGoogleSignUp && (
          <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-700">
              ✓ Signed in with Google. Please fill in the additional details below.
            </p>
          </div>
        )}
      </header>

      <div className="bg-white p-6 sm:p-8 rounded-lg shadow-md w-11/12 max-w-md">
        <form onSubmit={isGoogleSignUp ? handleGoogleFormSubmit : handleEmailSignUp}>
          <div className="flex gap-4 mb-4">
            <div className="flex flex-col w-1/2">
              <label className="text-sm font-semibold text-gray-700">First Name</label>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="border border-gray-300 rounded-md p-2 mt-1"
                required
              />
            </div>
            <div className="flex flex-col w-1/2">
              <label className="text-sm font-semibold text-gray-700">Last Name</label>
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="border border-gray-300 rounded-md p-2 mt-1"
                required
              />
            </div>
          </div>

          <div className="flex flex-col mb-4">
            <label className="text-sm font-semibold text-gray-700">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="border border-gray-300 rounded-md p-2 mt-1"
              required
              disabled={isGoogleSignUp}
            />
          </div>

          {!isGoogleSignUp && (
            <div className="flex flex-col mb-2">
              <label className="text-sm font-semibold text-gray-700">Set Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  validatePassword(e.target.value);
                }}
                className="border border-gray-300 rounded-md p-2 mt-1"
                required
              />
              {showRequirements && (
                <ul className="mt-2 text-xs text-gray-600 list-disc pl-5">
                  <li className={requirementsMet.length ? "text-green-600" : ""}>
                    At least 8 characters
                  </li>
                  <li className={requirementsMet.uppercase ? "text-green-600" : ""}>
                    Uppercase letter
                  </li>
                  <li className={requirementsMet.lowercase ? "text-green-600" : ""}>
                    Lowercase letter
                  </li>
                  <li className={requirementsMet.number ? "text-green-600" : ""}>
                    Number
                  </li>
                  <li className={requirementsMet.special ? "text-green-600" : ""}>
                    Special character
                  </li>
                </ul>
              )}
            </div>
          )}

          <div className="flex flex-col mb-4">
            <label className="text-sm font-semibold text-gray-700">School Year</label>
            <input
              type="text"
              value={schoolYear}
              onChange={(e) => setSchoolYear(e.target.value)}
              className="border border-gray-300 rounded-md p-2 mt-1"
              required
            />
          </div>

          <div className="flex flex-col mb-4">
            <label className="text-sm font-semibold text-gray-700">Level</label>
            <select
              value={level}
              onChange={(e) => setLevel(e.target.value)}
              className="border border-gray-300 rounded-md p-2 mt-1"
              required
            >
              <option value="Foundation">Foundation</option>
              <option value="Higher">Higher</option>
            </select>
          </div>

          <button
            type="submit"
            className="w-full bg-teal-600 text-white py-3 rounded-lg font-semibold hover:bg-teal-700 transition-colors duration-500 ease-in-out mb-4"
          >
            {isGoogleSignUp ? "Complete Sign-Up" : "Sign Up with Email"}
          </button>
        </form>

        {!isGoogleSignUp && (
          <button
            onClick={handleGoogleRedirect}
            className="w-full flex items-center justify-center bg-white border border-gray-300 rounded-lg py-3 font-semibold hover:bg-gray-100 transition-colors duration-500 ease-in-out"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              className="w-6 h-6 mr-3"
            >
              <path
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                fill="#4285F4"
              />
              <path
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                fill="#34A853"
              />
              <path
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                fill="#FBBC05"
              />
              <path
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                fill="#EA4335"
              />
            </svg>
            Sign Up with Google
          </button>
        )}

        <p className="mt-6 text-gray-600">
          Already have an account?{" "}
          <span
            onClick={() => navigate("/signin")}
            className="text-teal-600 hover:underline cursor-pointer"
          >
            Sign In
          </span>
        </p>
      </div>
    </div>
  );
};

export default SignUp;